import { mediaQueryLarge, isMobileBreakpoint } from "@theme/utilities";

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector("details");

    if (!(details instanceof HTMLDetailsElement))
      throw new Error("Details element not found");

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector("summary");

    if (!(summary instanceof HTMLElement))
      throw new Error("Summary element not found");

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === "true";
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === "true";
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === "true";
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener("keydown", this.#handleKeyDown, { signal });
    this.details.addEventListener("toggle", this.#handleToggle, { signal });
    this.summary.addEventListener("click", this.handleClick, { signal });
    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange, {
      signal,
    });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if (
      (isMobile && this.#disableOnMobile) ||
      (isDesktop && this.#disableOnDesktop)
    ) {
      event.preventDefault();
      return;
    }
  };

  #handleToggle = () => {
    if (!this.details.open) return;

    const closedSibling = this.#closeSiblingAccordions();
    if (!closedSibling) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#scrollOpenedAccordionIntoView();
      });
    });
  };

  #closeSiblingAccordions() {
    const groupName = this.dataset.accordionGroup;
    const accordions = groupName
      ? Array.from(document.querySelectorAll("accordion-custom")).filter(
          (accordion) => accordion.dataset.accordionGroup === groupName,
        )
      : Array.from(
          this.closest("[data-accordion-group]")?.querySelectorAll(
            "accordion-custom",
          ) ?? [],
        );

    let closedSibling = false;

    for (const accordion of accordions) {
      if (!(accordion instanceof AccordionCustom) || accordion === this)
        continue;

      if (accordion.details.open) {
        accordion.details.open = false;
        closedSibling = true;
      }
    }

    return closedSibling;
  }

  /**
   * Keeps the opened accordion header in view after a sibling collapses.
   * Collapsing tall accordion content above the viewport leaves scroll
   * position unchanged and can push the newly opened panel off-screen.
   */
  #scrollOpenedAccordionIntoView() {
    const summary = this.summary;
    const headerOffset =
      parseFloat(
        getComputedStyle(document.body).getPropertyValue(
          "--header-group-height",
        ),
      ) || 0;
    const { top, bottom } = summary.getBoundingClientRect();

    if (top < headerOffset || bottom > window.innerHeight) {
      summary.scrollIntoView({ behavior: "instant", block: "start" });
    }
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute("open-by-default-on-mobile")) ||
      (!isMobile && this.hasAttribute("open-by-default-on-desktop"));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === "Escape" && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get("accordion-custom")) {
  customElements.define("accordion-custom", AccordionCustom);
}
